import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { adminApi } from '../../api/client';

const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

const TIME_RANGE_DAYS: Record<string, number> = {
  '1h': 1,
  '24h': 1,
  '7d': 7,
  '30d': 30,
};

export function AdminMonitoringPage() {
  const [timeRange, setTimeRange] = useState('7d');
  const [autoRefresh, setAutoRefresh] = useState(true);

  const days = TIME_RANGE_DAYS[timeRange] || 7;

  const { data: systemHealth, refetch: refetchHealth } = useQuery({
    queryKey: ['admin', 'health'],
    queryFn: () => adminApi.getSystemHealth(),
    refetchInterval: autoRefresh ? 30000 : false,
  });

  const { data: metrics, isLoading: metricsLoading, dataUpdatedAt } = useQuery({
    queryKey: ['admin', 'monitoring-metrics', days],
    queryFn: () => adminApi.getMonitoringMetrics(days),
    refetchInterval: autoRefresh ? 60000 : false,
  });

  const { data: labelaryMetrics, isLoading: labelaryLoading } = useQuery({
    queryKey: ['admin', 'labelary-metrics', days],
    queryFn: () => adminApi.getLabelaryMonitoring(days),
    refetchInterval: autoRefresh ? 60000 : false,
  });

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        refetchHealth();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refetchHealth]);

  const lastUpdated = useMemo(() => {
    if (!dataUpdatedAt) return null;
    return new Date(dataUpdatedAt).toLocaleTimeString();
  }, [dataUpdatedAt]);

  const health = systemHealth;

  // Derive storage/API latency from health checks
  const checks = health?.checks as Record<string, { status: string; latencyMs?: number; error?: string }> | undefined;
  const storageLatency = checks?.tableStorage?.latencyMs ?? health?.storage?.latency;
  const storageHealthy = checks?.tableStorage?.status === 'healthy' || health?.storage?.status === 'healthy';
  const apiLatency = checks?.blobStorage?.latencyMs ?? health?.functions?.latency;
  const healthStatus = health?.status ?? 'unknown';

  const timeSeries = metrics?.timeSeries || [];
  const tenantDistribution = metrics?.tenantDistribution || [];
  const labelTypeDistribution = metrics?.labelTypeDistribution || [];
  const tenantActivity = metrics?.tenantActivity || [];
  const hasData = timeSeries.length > 0 || tenantActivity.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Monitoring</h1>
          {lastUpdated && (
            <p className="text-xs text-gray-400 mt-1">Last updated: {lastUpdated}</p>
          )}
        </div>
        <div className="flex items-center space-x-4">
          <select
            className="input w-32"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <option value="1h">Last 1d</option>
            <option value="24h">Last 1d</option>
            <option value="7d">Last 7d</option>
            <option value="30d">Last 30d</option>
          </select>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-600">Auto-refresh</span>
          </label>
        </div>
      </div>

      {/* Health Status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={`card border-l-4 ${
          healthStatus === 'healthy' ? 'border-l-green-500' : healthStatus === 'unknown' ? 'border-l-gray-400' : 'border-l-red-500'
        }`}>
          <p className="text-sm text-gray-500">System Status</p>
          <div className="flex items-center space-x-2">
            <span className={`w-3 h-3 rounded-full ${
              healthStatus === 'healthy' ? 'bg-green-500' : healthStatus === 'unknown' ? 'bg-gray-400' : 'bg-red-500'
            }`}></span>
            <span className="text-xl font-bold capitalize">{healthStatus}</span>
          </div>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500">Storage Latency</p>
          <p className="text-2xl font-bold text-gray-900">{storageLatency != null ? `${storageLatency}ms` : '--'}</p>
          {storageLatency != null && (
            <span className={`text-xs ${storageHealthy ? 'text-green-600' : 'text-yellow-600'}`}>
              {storageHealthy ? '● Normal' : '● Elevated'}
            </span>
          )}
        </div>
        <div className="card">
          <p className="text-sm text-gray-500">API Latency (Blob)</p>
          <p className="text-2xl font-bold text-gray-900">{apiLatency != null ? `${apiLatency}ms` : '--'}</p>
          {apiLatency != null && (
            <span className={`text-xs ${apiLatency < 100 ? 'text-green-600' : 'text-yellow-600'}`}>
              {apiLatency < 100 ? '● Normal' : '● Elevated'}
            </span>
          )}
        </div>
        <div className="card">
          <p className="text-sm text-gray-500">Labels ({timeRange})</p>
          <p className="text-2xl font-bold text-primary-600">
            {metrics?.totals?.labels?.toLocaleString() ?? '--'}
          </p>
          {metrics?.totals?.errors != null && metrics.totals.errors > 0 && (
            <span className="text-xs text-red-600">
              {metrics.totals.errors} errors
            </span>
          )}
        </div>
      </div>

      {/* Labelary Preview Usage */}
      <div className="card">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Labelary Preview Usage</h2>
            <p className="text-sm text-gray-600 mt-1">
              Tracks PDF/PNG preview rendering against the configured Labelary quota. ZPL generation does not use Labelary.
            </p>
          </div>
          {labelaryMetrics && (
            <span className={`px-3 py-1 text-xs font-medium rounded-full ${
              labelaryMetrics.upgrade.status === 'healthy'
                ? 'bg-green-100 text-green-800'
                : labelaryMetrics.upgrade.status === 'warning'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-red-100 text-red-800'
            }`}>
              {labelaryMetrics.upgrade.status === 'healthy' ? 'Free tier OK' : `Upgrade: ${labelaryMetrics.upgrade.recommendedTier}`}
            </span>
          )}
        </div>

        {labelaryLoading ? (
          <div className="text-sm text-gray-500 py-6 text-center">Loading Labelary metrics...</div>
        ) : labelaryMetrics ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="rounded-lg border border-gray-200 p-4">
                <p className="text-sm text-gray-500">Calls Today</p>
                <p className="text-2xl font-bold text-gray-900">
                  {labelaryMetrics.today.totalTrackedCalls.toLocaleString()}
                  <span className="text-sm font-normal text-gray-500"> / {labelaryMetrics.limits.daily.toLocaleString()}</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">{labelaryMetrics.today.usagePercent}% of daily quota</p>
              </div>
              <div className="rounded-lg border border-gray-200 p-4">
                <p className="text-sm text-gray-500">Remaining Today</p>
                <p className="text-2xl font-bold text-primary-600">{labelaryMetrics.today.remainingCalls.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">{labelaryMetrics.limits.perSecond}/sec burst limit</p>
              </div>
              <div className="rounded-lg border border-gray-200 p-4">
                <p className="text-sm text-gray-500">Upstream 429s Today</p>
                <p className={`text-2xl font-bold ${labelaryMetrics.today.upstream429Count > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                  {labelaryMetrics.today.upstream429Count.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {labelaryMetrics.today.lastUpstream429At
                    ? `Last: ${new Date(labelaryMetrics.today.lastUpstream429At).toLocaleString()}`
                    : 'No upstream rate limits today'}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 p-4">
                <p className="text-sm text-gray-500">Circuit Breaker</p>
                <p className="text-2xl font-bold capitalize text-gray-900">{labelaryMetrics.live.circuitState}</p>
                <p className="text-xs text-gray-500 mt-1">{labelaryMetrics.live.callsInLastSecond} calls in last second</p>
              </div>
            </div>

            <div className={`rounded-lg p-4 mb-6 ${
              labelaryMetrics.upgrade.status === 'healthy'
                ? 'bg-green-50 border border-green-200'
                : labelaryMetrics.upgrade.status === 'warning'
                  ? 'bg-yellow-50 border border-yellow-200'
                  : 'bg-red-50 border border-red-200'
            }`}>
              <p className="font-medium text-gray-900">Upgrade guidance</p>
              <p className="text-sm text-gray-700 mt-1">{labelaryMetrics.upgrade.message}</p>
              <ul className="text-xs text-gray-600 mt-3 space-y-1">
                <li>Free: 5,000 calls/day, 3/sec — current configured tier: {labelaryMetrics.limits.tier}</li>
                <li>Plus ($90/mo): 20,000 calls/day, 6/sec</li>
                <li>Business ($228/mo): 40,000 calls/day, 10/sec</li>
                <li>Alert email fires on Labelary HTTP 429 (deduped every 15 minutes) to jcrawford@edi-partners.com</li>
              </ul>
            </div>

            {labelaryMetrics.history.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">Daily preview usage</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={labelaryMetrics.history}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={(v) => v.substring(5)} />
                    <YAxis />
                    <Tooltip labelFormatter={(v) => `Date: ${v}`} />
                    <Legend />
                    <Area type="monotone" dataKey="successfulCalls" stroke="#4F46E5" fill="#4F46E5" fillOpacity={0.25} name="Successful calls" />
                    <Area type="monotone" dataKey="upstream429Count" stroke="#EF4444" fill="#EF4444" fillOpacity={0.25} name="Upstream 429s" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-gray-500 py-6 text-center">No Labelary metrics available yet.</div>
        )}
      </div>

      {/* Loading state */}
      {metricsLoading && (
        <div className="flex justify-center py-12">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            <span className="text-gray-500">Loading monitoring data...</span>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!metricsLoading && !hasData && (
        <div className="card text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No monitoring data</h3>
          <p className="mt-1 text-sm text-gray-500">
            Usage data will appear here once labels are generated through the API.
          </p>
        </div>
      )}

      {/* Charts Row 1 */}
      {!metricsLoading && hasData && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Label Generation Over Time */}
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Label Generation</h2>
              {timeSeries.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={timeSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" tickFormatter={(v) => v.substring(5)} />
                    <YAxis />
                    <Tooltip labelFormatter={(v) => `Date: ${v}`} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="labels"
                      stroke="#4F46E5"
                      fill="#4F46E5"
                      fillOpacity={0.3}
                      name="Labels Generated"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-gray-400">No time series data</div>
              )}
            </div>

            {/* Error Rate & Latency */}
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Errors & Latency</h2>
              {timeSeries.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={timeSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" tickFormatter={(v) => v.substring(5)} />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip labelFormatter={(v) => `Date: ${v}`} />
                    <Legend />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="errors"
                      stroke="#EF4444"
                      name="Errors"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="latency"
                      stroke="#F59E0B"
                      name="Latency (ms)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-gray-400">No time series data</div>
              )}
            </div>
          </div>

          {/* Charts Row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Labels by Tenant */}
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Labels by Tenant</h2>
              {tenantDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={tenantDistribution}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {tenantDistribution.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[250px] text-gray-400 text-sm">No tenant data</div>
              )}
            </div>

            {/* Labels by Type */}
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Labels by Type</h2>
              {labelTypeDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={labelTypeDistribution}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {labelTypeDistribution.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[250px] text-gray-400 text-sm">No label type data</div>
              )}
            </div>

            {/* Recent Errors */}
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Error Summary</h2>
              <div className="space-y-2 max-h-[250px] overflow-y-auto">
                {metrics?.totals?.errors && metrics.totals.errors > 0 ? (
                  <div className="space-y-3">
                    <div className="p-3 bg-red-50 rounded text-center">
                      <p className="text-2xl font-bold text-red-700">{metrics.totals.errors}</p>
                      <p className="text-sm text-red-600">total errors in last {timeRange}</p>
                    </div>
                    {tenantActivity.filter(t => t.errorsToday > 0).map((t) => (
                      <div key={t.tenantId} className="p-2 bg-red-50 rounded text-sm">
                        <p className="font-medium text-red-800">{t.tenantName}</p>
                        <p className="text-xs text-red-600">{t.errorsToday} errors today</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No recent errors</p>
                    <p className="text-xs">System operating normally</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Active Tenants Table */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Tenant Activity (Today)</h2>
            {tenantActivity.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-4">Tenant</th>
                    <th className="text-right py-2 px-4">Labels Today</th>
                    <th className="text-right py-2 px-4">Errors</th>
                    <th className="text-right py-2 px-4">Avg Latency</th>
                    <th className="text-left py-2 px-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tenantActivity.map((tenant) => (
                    <tr key={tenant.tenantId} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-4 font-medium">{tenant.tenantName}</td>
                      <td className="py-2 px-4 text-right">{tenant.labelsToday.toLocaleString()}</td>
                      <td className={`py-2 px-4 text-right ${tenant.errorsToday > 0 ? 'text-red-600' : ''}`}>
                        {tenant.errorsToday}
                      </td>
                      <td className="py-2 px-4 text-right">{tenant.avgLatencyMs}ms</td>
                      <td className="py-2 px-4">
                        <span className={`px-2 py-1 text-xs rounded ${
                          tenant.errorsToday === 0
                            ? 'bg-green-100 text-green-800'
                            : tenant.errorsToday > 10
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {tenant.errorsToday === 0 ? 'Healthy' : tenant.errorsToday > 10 ? 'Degraded' : 'Warning'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-center text-gray-400 py-8">No tenant activity recorded today</p>
            )}
          </div>
        </>
      )}

      {/* Application Insights Link */}
      <div className="card bg-primary-50 border border-primary-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-primary-900">Need More Details?</h3>
            <p className="text-sm text-primary-700">
              View detailed telemetry, custom queries, and alerts in Azure Application Insights.
            </p>
          </div>
          <a
            href="https://portal.azure.com/#blade/Microsoft_Azure_Monitoring/AzureMonitoringBrowseBlade/applicationInsights"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary"
          >
            Open App Insights →
          </a>
        </div>
      </div>
    </div>
  );
}
